"use client";

import { useState } from "react";
import Link from "next/link";
import { Brain, ChevronLeft, BookOpen, Zap, Trophy, Filter, Plus, Search } from "lucide-react";
import { ThemeToggle } from "@/lib/theme";
import { SAMPLE_WORDS, VocabWord } from "@/lib/vocab/sampleData";

type FilterTab = "all" | "new" | "learning" | "almost" | "mastered";

const tabConfig: { key: FilterTab; label: string; color: string }[] = [
  { key: "all", label: "All", color: "text-blue-600 border-blue-500" },
  { key: "new", label: "New", color: "text-gray-500 border-gray-300" },
  { key: "learning", label: "Learning", color: "text-yellow-600 border-yellow-400" },
  { key: "almost", label: "Almost There", color: "text-orange-600 border-orange-400" },
  { key: "mastered", label: "Mastered", color: "text-green-600 border-green-500" },
];

function masteryColor(level: number) {
  if (level >= 80) return "bg-green-500";
  if (level >= 50) return "bg-yellow-400";
  if (level >= 20) return "bg-orange-400";
  return "bg-gray-300";
}

function statusBadge(status: VocabWord["status"]) {
  switch (status) {
    case "new": return "bg-gray-100 text-gray-500";
    case "learning": return "bg-yellow-100 text-yellow-700";
    case "almost": return "bg-orange-100 text-orange-700";
    case "mastered": return "bg-green-100 text-green-700";
  }
}

function statusLabel(status: VocabWord["status"]) {
  switch (status) {
    case "new": return "New";
    case "learning": return "Learning";
    case "almost": return "Almost There";
    case "mastered": return "Mastered";
  }
}

export default function PoolPage() {
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");

  const filtered = SAMPLE_WORDS.filter(w => {
    const matchTab = activeTab === "all" || w.status === activeTab;
    const matchSearch = w.word.toLowerCase().includes(search.toLowerCase()) ||
      w.definition.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const counts = {
    all: SAMPLE_WORDS.length,
    new: SAMPLE_WORDS.filter(w => w.status === "new").length,
    learning: SAMPLE_WORDS.filter(w => w.status === "learning").length,
    almost: SAMPLE_WORDS.filter(w => w.status === "almost").length,
    mastered: SAMPLE_WORDS.filter(w => w.status === "mastered").length,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg-primary)" }}>
      {/* Nav */}
      <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/vocab" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <ChevronLeft size={16} /> Back
            </Link>
            <span style={{ color: "var(--border)" }}>|</span>
            <div className="flex items-center gap-2">
              <Brain size={18} className="text-blue-500" />
              <span className="font-semibold" style={{ color: "var(--text-primary)" }}>My Pool</span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Stats bar */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total in pool", value: counts.all, icon: BookOpen, color: "text-blue-500" },
            { label: "Due today", value: 5, icon: Zap, color: "text-yellow-500" },
            { label: "Almost mastered", value: counts.almost, icon: Filter, color: "text-orange-500" },
            { label: "Mastered", value: counts.mastered, icon: Trophy, color: "text-green-500" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="rounded-xl border p-4 text-center" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
              <Icon size={18} className={`${color} mx-auto mb-1`} />
              <div className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>{value}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <Link href="/vocab/study"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors">
            Study Flashcards
          </Link>
          <Link href="/vocab/quiz"
            className="px-4 py-2 rounded-lg font-medium text-sm hover:opacity-80 transition-opacity"
            style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
            Take Quiz
          </Link>
          <button className="ml-auto flex items-center gap-2 px-3 py-2 rounded-lg text-sm border hover:opacity-80 transition-opacity"
            style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
            <Plus size={14} /> Add Words
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
          {tabConfig.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors -mb-px ${
                activeTab === tab.key ? tab.color : "border-transparent"
              }`}
              style={activeTab !== tab.key ? { color: "var(--text-muted)" } : {}}
            >
              {tab.label}
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: "var(--bg-secondary)" }}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input
            type="text"
            placeholder="Search words or definitions…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)", color: "var(--text-primary)" }}
          />
        </div>

        {/* Word grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(word => (
            <Link key={word.id} href="/vocab/study"
              className="rounded-xl border p-4 hover:shadow-md transition-all hover:border-blue-200 cursor-pointer"
              style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{word.word}</span>
                  <span className="ml-2 text-xs" style={{ color: "var(--text-muted)" }}>{word.partOfSpeech}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(word.status)}`}>
                  {statusLabel(word.status)}
                </span>
              </div>
              <p className="text-xs mb-3 line-clamp-2" style={{ color: "var(--text-secondary)" }}>{word.shortDef}</p>
              <div>
                <div className="flex justify-between text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                  <span>Mastery</span>
                  <span>{word.masteryLevel}%</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ backgroundColor: "var(--bg-secondary)" }}>
                  <div className={`h-1.5 rounded-full transition-all ${masteryColor(word.masteryLevel)}`}
                    style={{ width: `${word.masteryLevel}%` }} />
                </div>
              </div>
              <p className="text-xs mt-2" style={{ color: "var(--text-faint)" }}>Last reviewed: {word.lastReviewed}</p>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Brain size={40} className="mx-auto mb-3 text-gray-300" />
            <p style={{ color: "var(--text-muted)" }}>No words found</p>
          </div>
        )}
      </div>
    </div>
  );
}
